import Link from "next/link";
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
  type Transition,
} from "framer-motion";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";

import {
  MENU_RESOURCE_LINKS,
  type NavHoverPanelId,
} from "@/chrome/chromeContent";
import NavPanelRow from "@/chrome/SiteNav/NavPanelRow";
import NavShopContent from "@/chrome/SiteNav/NavShopContent";
import { cn } from "@/lib/utils";
import { useTrack } from "@/analytics/events";

import { NAV_PANEL_MOTION } from "@/blueprint/tokens/Motion";

const readCssLength = (value: string) => Number.parseFloat(value) || 0;

const OPEN_EASE = [...NAV_PANEL_MOTION.openEase] as [
  number,
  number,
  number,
  number,
];
const CLOSE_EASE = [...NAV_PANEL_MOTION.closeEase] as [
  number,
  number,
  number,
  number,
];

type NavResourcesPanelBodyProps = {
  onNavigate: () => void;
  open: boolean;
  reducedMotion: boolean;
  panelWasClosed: boolean;
};

/** Divider and resources grid inside the hover panel. The surface overhangs
 *  its measured nav row by 8px on the left, so the 32px inset here resolves to
 *  the Figma divider's 24px visual inset. */
export const NavResourcesPanelBody = ({
  onNavigate,
  open,
  reducedMotion,
  panelWasClosed,
}: NavResourcesPanelBodyProps) => {
  const track = useTrack();

  return (
    <div className="flex w-full flex-col gap-xl px-xl pb-xl pt-[36px]">
      <NavPanelRow
        index={0}
        open={open}
        reducedMotion={reducedMotion}
        panelWasClosed={panelWasClosed}
      >
        <div className="border-t border-neutral1" />
      </NavPanelRow>
      {/* Two columns, so a stagger step is a visual row, not a link: the pair
          on a line arrives together. */}
      <div className="grid grid-cols-2 gap-x-l gap-y-s">
        {MENU_RESOURCE_LINKS.map((link, index) => (
          <NavPanelRow
            key={link.href}
            index={1 + Math.floor(index / 2)}
            open={open}
            reducedMotion={reducedMotion}
            panelWasClosed={panelWasClosed}
          >
            <Link
              href={link.href}
              {...(link.external === true
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              onClick={() => {
                track("nav_link_clicked", {
                  href: link.href,
                  label: link.label,
                  surface: "hover_panel",
                  nav_link_index: index,
                });
                onNavigate();
              }}
              className="text-nav-label text-content-secondary"
            >
              {link.label}
            </Link>
          </NavPanelRow>
        ))}
      </div>
    </div>
  );
};

export type NavPanelLayout = {
  left: number;
  top: number;
  width: number;
};

type NavHoverPanelProps = {
  panel: NavHoverPanelId | null;
  layout: NavPanelLayout | null;
  onNavigate: () => void;
  onMouseEnter: () => void;
  onPanelHeightChange: (panel: NavHoverPanelId, panelHeight: number) => void;
  containerRef?: RefObject<HTMLDivElement | null>;
};

const AnimatedPanelContent = ({
  children,
  panel,
  crossfadeMs,
  crossfade,
  onHeightChange,
}: {
  children: ReactNode;
  panel: NavHoverPanelId;
  crossfadeMs: number;
  /** False when this block arrives with the panel itself: the box and the
   *  staggered rows already carry that entrance, and a third fade layered over
   *  them only muddies it. The crossfade is for tab swaps. */
  crossfade: boolean;
  onHeightChange: (panel: NavHoverPanelId, height: number) => void;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const isPresent = useIsPresent();

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (content === null) return;

    const measure = () =>
      onHeightChange(panel, Math.round(content.getBoundingClientRect().height));

    measure();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry === undefined) return;

      const height =
        entry.borderBoxSize[0]?.blockSize ??
        entry.target.getBoundingClientRect().height;
      onHeightChange(panel, Math.round(height));
    });

    observer.observe(content, { box: "border-box" });
    return () => observer.disconnect();
  }, [onHeightChange, panel]);

  return (
    <motion.div
      ref={contentRef}
      aria-hidden={!isPresent}
      inert={!isPresent}
      /* Tab swap inside an already-open panel. Only the incoming block rises;
         the outgoing one fades in place, so the two do not cross in opposite
         directions over the same pixels. */
      initial={
        crossfade
          ? { opacity: 0, y: NAV_PANEL_MOTION.crossfadeRisePx }
          : { opacity: 1, y: 0 }
      }
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: (crossfade ? crossfadeMs : 0) / 1000,
        ease: OPEN_EASE,
      }}
      className="col-start-1 row-start-1 self-start"
    >
      {children}
    </motion.div>
  );
};

const NavHoverPanel = ({
  panel,
  layout,
  onNavigate,
  onMouseEnter,
  onPanelHeightChange,
  containerRef,
}: NavHoverPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const lastPanelRef = useRef<NavHoverPanelId>("resources");
  const [contentHeights, setContentHeights] = useState<
    Partial<Record<NavHoverPanelId, number>>
  >({});
  // A ResizeObserver firing mid-morph would retarget the running height tween,
  // and a retargeted tween restarts with a fresh duration — a visible hitch.
  // Measurements taken while the box is moving are held here and applied once
  // it has settled. The first measurement of a panel is never deferred: it
  // happens in the child's layout effect, which runs before the effect below
  // arms the guard, so the morph always starts with a correct target.
  const pendingHeightsRef = useRef<Partial<Record<NavHoverPanelId, number>>>(
    {},
  );
  const morphingRef = useRef(false);
  const [clearanceHeight, setClearanceHeight] = useState(0);
  const [isVisible, setIsVisible] = useState(panel !== null);
  const reducedMotion = useReducedMotion() === true;
  const open = panel !== null;
  const effectiveCrossfadeMs = reducedMotion ? 0 : NAV_PANEL_MOTION.crossfadeMs;
  const transition: Transition = reducedMotion
    ? { duration: 0 }
    : {
        duration:
          (open ? NAV_PANEL_MOTION.openMs : NAV_PANEL_MOTION.closeMs) / 1000,
        ease: open ? OPEN_EASE : CLOSE_EASE,
      };

  useEffect(() => {
    if (panel !== null) {
      lastPanelRef.current = panel;
    }
  }, [panel]);
  const shownPanel = panel ?? lastPanelRef.current;
  const shownContentHeight = contentHeights[shownPanel];
  const shownPanelHeight =
    shownContentHeight === undefined
      ? undefined
      : clearanceHeight + shownContentHeight;

  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      if (containerRef !== undefined) containerRef.current = node;
    },
    [containerRef],
  );

  const commitPendingHeights = useCallback(() => {
    const pending = pendingHeightsRef.current;
    const entries = Object.entries(pending) as [NavHoverPanelId, number][];
    if (entries.length === 0) return;
    pendingHeightsRef.current = {};
    setContentHeights((current) => {
      let next = current;
      entries.forEach(([panelId, contentHeight]) => {
        if (next[panelId] !== contentHeight) {
          next = { ...next, [panelId]: contentHeight };
        }
      });
      return next;
    });
  }, []);

  const settleMorph = useCallback(() => {
    morphingRef.current = false;
    commitPendingHeights();
  }, [commitPendingHeights]);

  const handleContentHeightChange = useCallback(
    (panelId: NavHoverPanelId, contentHeight: number) => {
      const panelElement = panelRef.current;
      if (panelElement !== null) {
        setClearanceHeight(
          Math.round(readCssLength(getComputedStyle(panelElement).paddingTop)),
        );
      }
      if (morphingRef.current) {
        pendingHeightsRef.current[panelId] = contentHeight;
        return;
      }
      setContentHeights((current) =>
        current[panelId] === contentHeight
          ? current
          : { ...current, [panelId]: contentHeight },
      );
    },
    [],
  );

  useLayoutEffect(() => {
    const panelElement = panelRef.current;
    if (panelElement !== null) {
      setClearanceHeight(
        Math.round(readCssLength(getComputedStyle(panelElement).paddingTop)),
      );
    }
    if (panel !== null) setIsVisible(true);

    // onAnimationComplete is the primary release, but it never fires when the
    // morph is a no-op (two panels of identical height with the box already
    // open), which would strand the guard on and freeze heights for good.
    morphingRef.current = true;
    const timer = window.setTimeout(
      settleMorph,
      (panel === null ? NAV_PANEL_MOTION.closeMs : NAV_PANEL_MOTION.openMs) +
        50,
    );
    return () => window.clearTimeout(timer);
  }, [panel, settleMorph]);

  useLayoutEffect(() => {
    (Object.entries(contentHeights) as [NavHoverPanelId, number][]).forEach(
      ([panelId, contentHeight]) =>
        onPanelHeightChange(panelId, clearanceHeight + contentHeight),
    );
  }, [clearanceHeight, contentHeights, onPanelHeightChange]);

  return (
    <motion.div
      ref={setPanelRef}
      aria-hidden={panel === null}
      inert={panel === null}
      initial={false}
      animate={{
        opacity: panel === null ? 0 : 1,
        // Collapse to nothing on the way out. Holding the open height meant the
        // box only ever expanded on its first appearance — every reopen after
        // that was a bare fade at full size, with no height movement to feel.
        ...(shownPanelHeight === undefined
          ? {}
          : { height: open ? shownPanelHeight : 0 }),
      }}
      style={{
        left: layout?.left ?? -4,
        top: layout?.top ?? -28,
        width: layout?.width ?? "calc(100% + 32px)",
      }}
      transition={transition}
      onAnimationComplete={() => {
        settleMorph();
        if (panel === null) setIsVisible(false);
      }}
      onMouseEnter={onMouseEnter}
      className={cn(
        "absolute z-[5] overflow-hidden rounded-l font-ppMori",
        "pt-[calc(68px+var(--text-paragraph-sm--line-height))]",
        panel === null && "pointer-events-none",
        isVisible ? "visible" : "invisible",
      )}
    >
      <div className="grid">
        <AnimatePresence initial={false}>
          <AnimatedPanelContent
            key={shownPanel}
            panel={shownPanel}
            crossfadeMs={effectiveCrossfadeMs}
            crossfade={isVisible}
            onHeightChange={handleContentHeightChange}
          >
            {shownPanel === "resources" ? (
              <NavResourcesPanelBody
                onNavigate={onNavigate}
                open={open}
                reducedMotion={reducedMotion}
                panelWasClosed={!isVisible}
              />
            ) : (
              <NavShopContent
                variant="panel"
                onNavigate={onNavigate}
                panelOpen={open}
                reducedMotion={reducedMotion}
                panelWasClosed={!isVisible}
              />
            )}
          </AnimatedPanelContent>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default NavHoverPanel;
